import remarkParse from "remark-parse";
import remarkBreaks from "remark-breaks";
import { unified } from "unified";
import { Content, Heading } from "mdast";
import { ReactElement, ReactNode } from "react";
import { remarkInline } from "./remarkInline";
import { HighlightText } from "@/text/HighlightText";

const parser = unified()
  .use(remarkParse)
  .use(remarkBreaks)
  .use(
    remarkInline([
      { tag: "++", type: "ins" },
      { tag: "~", type: "sub" },
      { tag: "^", type: "sup" },
    ]),
  );

declare module "mdast" {
  interface StaticPhrasingContentMap {
    ins: { type: "ins"; children: PhrasingContent[] };
    sub: { type: "sub"; children: PhrasingContent[] };
    sup: { type: "sup"; children: PhrasingContent[] };
  }
}

function render(
  options: { enableLinks?: boolean; highlightWords?: string[] },
  c: Content,
  key: number,
): ReactNode {
  const children = "children" in c ? c.children.map((cc, i) => render(options, cc, i)) : null;
  switch (c.type) {
    case "paragraph":
      return <p key={key}>{children}</p>;
    case "list":
      return c.ordered ? <ol key={key}>{children}</ol> : <ul key={key}>{children}</ul>;
    case "listItem":
      return <li key={key}>{children}</li>;
    case "break":
      return <br key={key} />;
    case "emphasis":
      return <em key={key}>{children}</em>;
    case "strong":
      return <strong key={key}>{children}</strong>;
    case "text":
      return options.highlightWords?.length ? (
        <HighlightText key={key} value={c.value} words={options.highlightWords} />
      ) : (
        c.value
      );
    case "sup":
      return <sup key={key}>{children}</sup>;
    case "sub":
      return <sub key={key}>{children}</sub>;
    case "ins":
      return <ins key={key}>{children}</ins>;
    case "heading":
      return mapToHeading(c, key, children);
    case "html":
      return c.value;
    case "link":
      if (options.enableLinks) {
        return c.url?.startsWith("/") ? (
          <a key={key} href={c.url}>
            {children}
          </a>
        ) : (
          <a key={key} href={c.url} target={"_blank"}>
            {children}
          </a>
        );
      }
      return `[${children}](${c.url})`;
    default:
      return null;
  }
}

export function markdownToHtml(
  markdown: string,
  options: { enableLinks?: boolean; highlightWords?: string[] } = {},
): ReactElement {
  const node = parser.runSync(parser.parse(markdown));
  return <>{node.children.map((c, i) => render(options, c, i))}</>;
}

const trailingWhitespacePattern = /((?:&#32;)+)(\*{1,2})/g;
const leadingWhitespacePattern = /(\*{1,2})((?:&#32;)+)/g;
const escapedWhitespacePattern = /&#32;/g;

export function normalizeLexicalMarkdown(markdown: string): string {
  return markdown
    .replaceAll(
      trailingWhitespacePattern,
      (_, g1, g2) => `${g2}${g1.replaceAll(escapedWhitespacePattern, " ")}`,
    )
    .replaceAll(
      leadingWhitespacePattern,
      (_, g1, g2) => `${g2.replaceAll(escapedWhitespacePattern, " ")}${g1}`,
    );
}

function mapToHeading(c: Heading, key: number, children: ReactNode): ReactElement {
  switch (c.depth) {
    case 1:
      return <h1 key={key}>{children}</h1>;
    case 2:
      return <h2 key={key}>{children}</h2>;
    case 3:
      return <h3 key={key}>{children}</h3>;
    case 4:
      return <h4 key={key}>{children}</h4>;
    case 5:
      return <h5 key={key}>{children}</h5>;
    case 6:
      return <h6 key={key}>{children}</h6>;
  }
}
